import { useEffect, useState } from 'react';
import type { HintMove } from '../rules';
import { v } from '../constants';

// Fixed-position SVG overlay that draws an animated dashed line from
// a bag tile to a target cell. The dashes "march" along the path so
// the user reads the suggested motion (drag this tile to that cell).
//
// Endpoint coordinates are derived from `getBoundingClientRect` on
// the tile's `[data-tile-id]` button and the cell's `[data-cell-idx]`
// SVG group — the same selectors the drag system uses.
//
// `scopeSelector` constrains queries to a subtree (e.g. the tutorial
// overlay). When the tutorial is open the main app's bag + board are
// still in the DOM behind it, so an un-scoped `document.querySelector`
// returns the wrong element. Default `:root` matches anywhere.
export function HintArrow({ hint, scopeSelector = ':root' }: { hint: HintMove | null; scopeSelector?: string }) {
  const [coords, setCoords] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  useEffect(() => {
    if (!hint || hint.kind !== 'place') { setCoords(null); return; }
    // Continuously re-measure via rAF: board / bag sizes are driven by
    // ResizeObservers that fire AFTER useEffect, and CSS media-query
    // breakpoint crossings (padding, layout reflow) don't fire `resize`.
    // A 60Hz rAF loop tracks every layout shift without us enumerating
    // each trigger. Two getBoundingClientRect calls per frame is cheap.
    let rafId = 0;
    let lastKey = '';
    const tick = () => {
      const scope = document.querySelector(scopeSelector) ?? document;
      const tileEl = scope.querySelector(`[data-tile-id="${hint.tileId}"]`);
      const cellG = scope.querySelector(`[data-cell-idx="${hint.cellIdx}"]`);
      // The cell `<g>` contains an animated hint-pulse polygon that
      // scales each frame — reading the `<g>`'s rect would track the
      // pulse and the arrow tip would jitter. Anchor to the first child
      // polygon (the cell background, rendered before any animation
      // layers) so the rect stays stable across the pulse cycle.
      const cellEl = cellG?.querySelector('polygon') ?? cellG;
      if (!tileEl || !cellEl) {
        if (lastKey !== '__null') { setCoords(null); lastKey = '__null'; }
      } else {
        const tr = (tileEl as Element).getBoundingClientRect();
        const cr = (cellEl as Element).getBoundingClientRect();
        // Skip the setState when the rect hasn't moved enough to matter
        // (sub-pixel drift from layout passes is invisible and avoids
        // a render-storm).
        const next = {
          x1: tr.left + tr.width / 2,
          y1: tr.top + tr.height / 2,
          x2: cr.left + cr.width / 2,
          y2: cr.top + cr.height / 2,
        };
        const key = `${next.x1.toFixed(1)},${next.y1.toFixed(1)},${next.x2.toFixed(1)},${next.y2.toFixed(1)}`;
        if (key !== lastKey) { setCoords(next); lastKey = key; }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [hint, scopeSelector]);

  if (!hint || hint.kind !== 'place' || !coords) return null;

  const { x1, y1, x2, y2 } = coords;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  // Pull endpoints inward so the dashes don't poke through the tile
  // and cell hit areas.
  const inset = Math.min(28, len * 0.18);
  const ux = dx / len;
  const uy = dy / len;
  const sx = x1 + ux * inset;
  const sy = y1 + uy * inset;
  const ex = x2 - ux * inset;
  const ey = y2 - uy * inset;

  // Arrowhead at the cell end — two short legs angled back.
  const headLen = 9;
  const headAng = 0.45;
  const cosA = Math.cos(headAng);
  const sinA = Math.sin(headAng);
  const head1x = ex - headLen * (ux * cosA + uy * sinA);
  const head1y = ey - headLen * (uy * cosA - ux * sinA);
  const head2x = ex - headLen * (ux * cosA - uy * sinA);
  const head2y = ey - headLen * (uy * cosA + ux * sinA);

  return (
    <svg
      className="hm-hint-arrow"
      style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 150 }}
    >
      <line
        x1={sx} y1={sy} x2={ex} y2={ey}
        stroke={v('accent')}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray="6 8"
        className="hm-hint-arrow-line"
      />
      <polyline
        points={`${head1x},${head1y} ${ex},${ey} ${head2x},${head2y}`}
        fill="none"
        stroke={v('accent')}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
